import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { rulesApi, RULES_LOCALE } from "../../utils/rulesApi";

/**
 * The code-defined rule catalog (`GET admin/rules/types`) and its lookups, loaded once per
 * session and shared by the rule-set editor and the add-rule dialog.
 */
const initialState = {
  locale: null,
  loading: false,
  error: null,
  types: [],
  categories: [],
  heights: [],
  fullCodeModes: [],
};

export const loadRuleCatalog = createAsyncThunk(
  "ruleCatalog/load",
  async () => {
    const [types, categories, heights, fullCodeModes] = await Promise.all([
      rulesApi.types(),
      rulesApi.categories(),
      rulesApi.heights(),
      rulesApi.fullCodeModes(),
    ]);
    return {
      locale: RULES_LOCALE,
      types: types || [],
      categories: categories || [],
      heights: heights || [],
      fullCodeModes: fullCodeModes || [],
    };
  },
  {
    condition: (_, { getState }) => {
      const { ruleCatalog } = getState();
      return !ruleCatalog.loading && ruleCatalog.locale !== RULES_LOCALE;
    },
  },
);

export const ruleCatalogSlice = createSlice({
  name: "ruleCatalog",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadRuleCatalog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadRuleCatalog.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.locale = payload.locale;
        state.types = payload.types;
        state.categories = payload.categories;
        state.heights = payload.heights;
        state.fullCodeModes = payload.fullCodeModes;
      })
      .addCase(loadRuleCatalog.rejected, (state, { error }) => {
        state.loading = false;
        state.error = error?.message || "Could not load the rule catalog";
      });
  },
});

export const selectRuleCatalog = (state) => state.ruleCatalog;

export const selectRuleTypesByKey = (state) =>
  Object.fromEntries(state.ruleCatalog.types.map((type) => [type.key, type]));

export default ruleCatalogSlice.reducer;
